import moment from 'moment';
import { RAID_METADATA, SEASONS, SEASONS_BY_SLUG } from './enums';
import { DEFAULT_STATISTICS_SEASON_REGION } from './statisticsUtils';
import { TSeasonDef } from '../../../shared/gameConfig/mythicPlus';
import {
  getKeystoneLeaderboardPeriod,
  getRaidDifficultyReleaseDateForRegion,
  getRaidWeekForRaidAndRegion
} from '../../../shared/common/utils';
import { RAID_METADATA_BY_SLUG } from './enums';
import { TRaidMetadata } from '../../../shared/gameConfig/wowretail/types';

// TODO: Raid and season week related functions are very similar, maybe we can merge them?

export type TSeasonRegionIdentifier = {
  season: string;
  region?: string;
};

export type TRaidRegionIdentifier = {
  raid: string;
  region?: string;
};

export const convertRegionToOffsetRegion = (region?: string): string => {
  if (!region) {
    return 'us';
  }

  return region;
};

// Reset epochs differ per region: US resets first (Tue), KR/TW/CN last (Wed 23:00). So a given week
// starts earliest in US but ends latest in KR/TW/CN. For a global view (no region), an explicit region
// collapses to itself, but an undefined region must span every region so week-end / week-in-past reflect
// the LAST region to finish, not just US.
const CONCRETE_REGIONS = ['us', 'eu', 'kr', 'tw', 'cn'];

const resolveOffsetRegions = (region?: string): string[] =>
  region ? [convertRegionToOffsetRegion(region)] : CONCRETE_REGIONS;

/**
 * Returns the week of the season by period
 */
export const getSeasonWeekByPeriod = ({ season, region = '' }: TSeasonRegionIdentifier, period: number): number => {
  const seasonStartPeriod = getKeystoneLeaderboardPeriod(
    convertRegionToOffsetRegion(region),
    SEASONS_BY_SLUG.get(season)!.starts[convertRegionToOffsetRegion(region)]
  );
  return period - seasonStartPeriod + 1;
};

/**
 * Returns the week of the raid by period
 */
export const getRaidWeekByPeriod = ({ raid, region = '' }: TRaidRegionIdentifier, period: number): number => {
  const raidStartPeriod = getKeystoneLeaderboardPeriod(
    convertRegionToOffsetRegion(region),
    RAID_METADATA_BY_SLUG[raid].starts[convertRegionToOffsetRegion(region)]
  );
  return period - raidStartPeriod + 1;
};

/**
 * Returns the region-specific keystone period for a given season week.
 *
 * Period numbers are region-specific (each region has its own reset epoch), so the
 * same season week maps to different period numbers across regions. This is the single
 * source of the week→period formula shared by the fact-generation tool (write path) and
 * the spec-loadout aggregation reads (read path) so they always agree per region.
 */
export const getSeasonPeriodForWeek = ({ season, region = '' }: TSeasonRegionIdentifier, week: number): number => {
  const seasonStart = SEASONS_BY_SLUG.get(season)!.starts[convertRegionToOffsetRegion(region)];
  return getKeystoneLeaderboardPeriod(convertRegionToOffsetRegion(region), seasonStart.clone().add(week - 1, 'weeks'));
};

/**
 * Returns the region-specific keystone period for a given raid week. See getSeasonPeriodForWeek.
 */
export const getRaidPeriodForWeek = ({ raid, region = '' }: TRaidRegionIdentifier, week: number): number => {
  const raidStart = RAID_METADATA_BY_SLUG[raid].starts[convertRegionToOffsetRegion(region)];
  return getKeystoneLeaderboardPeriod(convertRegionToOffsetRegion(region), raidStart.clone().add(week - 1, 'weeks'));
};

// The `raid_encounters` search index numbers weeks per difficulty, while Talent Builds carries
// heroic-relative raid weeks. Mythic opens later, so its search weeks need this offset.
export const getRaidDifficultyWeekOffset = ({
  raid,
  region,
  difficulty
}: TRaidRegionIdentifier & { difficulty: string }): number => {
  const offsetRegion = region && region !== 'world' ? region : 'us';
  const releaseDate = getRaidDifficultyReleaseDateForRegion(raid, offsetRegion, difficulty);
  if (!releaseDate) {
    return 0;
  }

  const heroicWeekAtRelease = getRaidWeekForRaidAndRegion(raid, offsetRegion, 'heroic', releaseDate);
  return heroicWeekAtRelease != null ? heroicWeekAtRelease - 1 : 0;
};

/**
 * Returns the start date of the week of the season
 */
export const getSeasonWeekStartDate = ({ season, region = '' }: TSeasonRegionIdentifier, week: number): string => {
  const seasonStart = SEASONS_BY_SLUG.get(season)!.starts[convertRegionToOffsetRegion(region)];
  return seasonStart
    .clone()
    .add(week - 1, 'weeks')
    .toISOString();
};

/**
 * Returns the start date of the week of the raid
 */
export const getRaidWeekStartDate = ({ raid, region = '' }: TRaidRegionIdentifier, week: number): string => {
  const raidStart = RAID_METADATA_BY_SLUG[raid].starts[convertRegionToOffsetRegion(region)];
  return raidStart
    .clone()
    .add(week - 1, 'weeks')
    .toISOString();
};

/**
 * Returns the end date of the week of the season; if the week is ongoing or in the future, the current date is returned.
 */
export const getSeasonWeekEndDate = ({ season, region = '' }: TSeasonRegionIdentifier, week: number): string => {
  const seasonDef = SEASONS_BY_SLUG.get(season)!;
  const latestWeekEnd = moment.max(
    resolveOffsetRegions(region).map((r) =>
      moment.min(seasonDef.starts[r].clone().add(week, 'weeks'), seasonDef.ends[r])
    )
  );
  return moment.min(moment.utc(), latestWeekEnd).toISOString();
};

/**
 * Returns the end date of the week of the raid;
 * if the week is ongoing or in the future, the current date is returned.
 */
export const getRaidWeekEndDate = ({ raid, region = '' }: TRaidRegionIdentifier, week: number): string => {
  const raidDef = RAID_METADATA_BY_SLUG[raid];
  const latestWeekEnd = moment.max(
    resolveOffsetRegions(region).map((r) => moment.min(raidDef.starts[r].clone().add(week, 'weeks'), raidDef.ends[r]))
  );
  return moment.min(moment.utc(), latestWeekEnd).toISOString();
};

/**
 * Returns the number of weeks in a season; If the season is ongoing, it will return the number of current week
 */
export const getTotalWeeksOfSeason = ({
  season: seasonSlug,
  region = DEFAULT_STATISTICS_SEASON_REGION
}: TSeasonRegionIdentifier): number => {
  const season = SEASONS_BY_SLUG.get(seasonSlug)!;

  const isSeasonStarted = moment.utc().isAfter(season.starts[convertRegionToOffsetRegion(region)]);

  if (!isSeasonStarted) {
    return 0;
  }

  const seasonEnd = season.ends[convertRegionToOffsetRegion(region)];
  const endDate = moment.utc().isBefore(seasonEnd) ? moment.utc() : seasonEnd;
  const startDate = season.starts[convertRegionToOffsetRegion(region)];
  const differenceInWeeks = endDate.diff(startDate, 'week', true);
  return Math.max(Math.ceil(differenceInWeeks), 1);
};

const calculateWeekByDate = (
  startFirstWeekDate: moment.Moment,
  endLastWeekDate: moment.Moment,
  date: string
): number => {
  const targetDate = moment.utc(date);

  if (!targetDate.isValid()) {
    return 0;
  }

  if (targetDate.isBefore(startFirstWeekDate) || targetDate.isAfter(endLastWeekDate)) {
    return 0;
  }

  const differenceInWeeks = targetDate.diff(startFirstWeekDate, 'week', true);
  return Math.max(Math.ceil(differenceInWeeks), 1);
};

export const getSeasonWeekByDate = (
  { season: seasonSlug, region = '' }: TSeasonRegionIdentifier,
  date: string
): number => {
  const season = SEASONS_BY_SLUG.get(seasonSlug)!;
  const seasonStart = season.starts[convertRegionToOffsetRegion(region)];
  const seasonEnd = season.ends[convertRegionToOffsetRegion(region)];

  return calculateWeekByDate(seasonStart, seasonEnd, date);
};

export const getRaidWeekByDate = ({ raid: raidSlug, region = '' }: TRaidRegionIdentifier, date: string): number => {
  const raid = RAID_METADATA_BY_SLUG[raidSlug];
  const raidStart = raid.starts[convertRegionToOffsetRegion(region)];
  const raidEnd = raid.ends[convertRegionToOffsetRegion(region)];

  return calculateWeekByDate(raidStart, raidEnd, date);
};

const calculatePeriodRangeByWeeks = (
  firstWeekStartDate: moment.Moment,
  lastWeekEndDate: moment.Moment,
  region: string,
  startWeek: number,
  endWeek: number
): { periodStart: number; periodEnd: number } => {
  const maxDate = moment.utc().isBefore(lastWeekEndDate) ? moment.utc() : lastWeekEndDate;

  let endDate = firstWeekStartDate.clone().add(endWeek - 1, 'weeks');

  if (maxDate.isBefore(endDate)) {
    endDate = maxDate;
  }

  return {
    periodStart: getKeystoneLeaderboardPeriod(
      convertRegionToOffsetRegion(region),
      firstWeekStartDate.clone().add(startWeek - 1, 'weeks')
    ),
    periodEnd: getKeystoneLeaderboardPeriod(convertRegionToOffsetRegion(region), endDate)
  };
};

export const getPeriodRangeBySeasonsWeeks = ({
  season: seasonSlug,
  region = '',
  seasonStartWeek,
  seasonEndWeek
}: {
  seasonStartWeek: number;
  seasonEndWeek: number;
} & TSeasonRegionIdentifier): { periodStart: number; periodEnd: number } => {
  const season = SEASONS_BY_SLUG.get(seasonSlug)!;
  const start = season.starts[convertRegionToOffsetRegion(region)];
  const end = season.ends[convertRegionToOffsetRegion(region)];

  return calculatePeriodRangeByWeeks(start, end, region, seasonStartWeek, seasonEndWeek);
};

export const getPeriodRangeByRaidWeeks = ({
  raid: raidSlug,
  region = '',
  raidStartWeek,
  raidEndWeek
}: {
  raidStartWeek: number;
  raidEndWeek: number;
} & TRaidRegionIdentifier): { periodStart: number; periodEnd: number } => {
  const raid = RAID_METADATA_BY_SLUG[raidSlug];
  const start = raid.starts[convertRegionToOffsetRegion(region)];
  const end = raid.ends[convertRegionToOffsetRegion(region)];

  return calculatePeriodRangeByWeeks(start, end, region, raidStartWeek, raidEndWeek);
};

export type TActivityPeriodRangeInput = {
  season?: string;
  raid?: string;
  region?: string;
  seasonWeekStart: number;
  seasonWeekEnd: number;
};

export const resolveActivityPeriodRange = (
  input: TActivityPeriodRangeInput
): { periodStart: number; periodEnd: number } => {
  if (input.season) {
    return getPeriodRangeBySeasonsWeeks({
      season: input.season,
      region: input.region,
      seasonStartWeek: input.seasonWeekStart,
      seasonEndWeek: input.seasonWeekEnd
    });
  }

  if (input.raid) {
    return getPeriodRangeByRaidWeeks({
      raid: input.raid,
      region: input.region,
      raidStartWeek: input.seasonWeekStart,
      raidEndWeek: input.seasonWeekEnd
    });
  }

  throw new Error('Season or Raid is required');
};

/**
 * Returns true if the season is finished
 */
export const checkIsSeasonFinished = ({ season, region = '' }: TSeasonRegionIdentifier): boolean => {
  const now = moment.utc();
  return now.isAfter(SEASONS_BY_SLUG.get(season)!.ends[convertRegionToOffsetRegion(region)]);
};

/**
 * Returns the entire season weeks
 */
export const getEntireSeasonWeeks = ({
  season,
  region = ''
}: TSeasonRegionIdentifier): {
  seasonWeekStart: number;
  seasonWeekEnd: number;
} => {
  return { seasonWeekStart: 1, seasonWeekEnd: getTotalWeeksOfSeason({ season, region }) };
};

export const getEntireRaidWeeks = ({
  raid,
  region = ''
}: TRaidRegionIdentifier): {
  raidWeekStart: number;
  raidWeekEnd: number;
} => {
  return { raidWeekStart: 1, raidWeekEnd: getTotalWeeksOfRaid({ raid, region }) };
};

export const findSeasonByDate = (date: string): TSeasonDef | null => {
  if (!moment(date).isValid()) {
    return null;
  }

  const seasons = SEASONS.filter(
    (season) => moment(date).isSameOrAfter(season.starts.us) && moment(date).isBefore(season.ends.us)
  );

  return seasons.find((season) => season.isMainSeason) || seasons[0] || null;
};

export const isEntireSeasonWeeks = ({
  season,
  region = '',
  seasonWeekStart,
  seasonWeekEnd
}: TSeasonRegionIdentifier & {
  seasonWeekStart: number;
  seasonWeekEnd: number;
}): boolean => {
  return seasonWeekStart === 1 && seasonWeekEnd === getTotalWeeksOfSeason({ season, region });
};

export const getIsSeasonWeekInPast = ({ season, region = '' }: TSeasonRegionIdentifier, week: number): boolean => {
  const seasonDef = SEASONS_BY_SLUG.get(season)!;
  const latestWeekEnd = moment.max(
    resolveOffsetRegions(region).map((r) => seasonDef.starts[r].clone().add(week, 'weeks'))
  );
  return moment.utc().isAfter(latestWeekEnd);
};

export const getIsRaidWeekInPast = ({ raid, region = '' }: TRaidRegionIdentifier, week: number): boolean => {
  const raidStart = RAID_METADATA_BY_SLUG[raid].starts[convertRegionToOffsetRegion(region)];
  const endDate = raidStart.clone().add(week, 'weeks');
  return moment.utc().isAfter(endDate);
};

export const findRaidByDate = (date: string): TRaidMetadata | null => {
  if (!moment(date).isValid()) {
    return null;
  }

  const raids = RAID_METADATA.filter(
    (raid) => moment(date).isSameOrAfter(raid.starts.us) && moment(date).isBefore(raid.ends.us)
  );

  return raids[0];
};

// TODO: looks similar to getTotalWeeksOfSeason, maybe we can merge them?
export const getTotalWeeksOfRaid = ({ raid: raidSlug, region = '' }: TRaidRegionIdentifier): number => {
  const raid = RAID_METADATA_BY_SLUG[raidSlug];

  const isRaidStarted = moment.utc().isAfter(raid.starts[convertRegionToOffsetRegion(region)]);

  if (!isRaidStarted) {
    return 0;
  }

  const raidEnd = raid.ends[convertRegionToOffsetRegion(region)];
  const endDate = moment.utc().isBefore(raidEnd) ? moment.utc() : raidEnd;
  const startDate = raid.starts[convertRegionToOffsetRegion(region)];
  const differenceInWeeks = endDate.diff(startDate, 'week', true);
  return Math.max(Math.ceil(differenceInWeeks), 1);
};

export const getCurrentPeriod = (region = 'us'): number => {
  return getKeystoneLeaderboardPeriod(convertRegionToOffsetRegion(region), moment.utc());
};

/**
 * For the resolved regions, returns the lowest in-progress week (weeks <= 0 are out-of-range and ignored),
 * or null when none is ongoing. For a global (no region) view this is the LAST region to finish the week,
 * mirroring getSeasonWeekEndDate / getIsSeasonWeekInPast; using the US default alone would advance the band
 * as soon as US resets, before the other regions have finished the current week.
 */
const resolveCurrentActivityWeek = (regions: string[], weekForRegion: (region: string) => number): number | null => {
  const weeks = regions.map(weekForRegion).filter((week) => week > 0);
  return weeks.length ? Math.min(...weeks) : null;
};

/**
 * Returns the week currently in progress (the week containing "now") for the active mode, or null when
 * nothing is ongoing — the season/raid hasn't started yet, or has already finished. Season takes
 * precedence over raid, matching resolveActivityPeriodRange.
 */
export const getCurrentActivityWeek = ({
  season,
  raid,
  region
}: {
  season?: string;
  raid?: string;
  region?: string;
}): number | null => {
  const now = moment.utc().toISOString();

  if (season) {
    return resolveCurrentActivityWeek(resolveOffsetRegions(region), (r) => getSeasonWeekByDate({ season, region: r }, now));
  }

  if (raid) {
    return resolveCurrentActivityWeek(resolveOffsetRegions(region), (r) => getRaidWeekByDate({ raid, region: r }, now));
  }

  return null;
};
